import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import SQCAnalystChart from '../SQCAnalystChart';
import SQCInstrumentsChart from '../SQCInstrumentsChart';

const ComparisonSection = ({
  localSearchFinished,
  currentPersonnelNames,
  currentPersonnelDots,
  chartScalars,
  chartData,
  renderChartLabelValue,
  currentMethodComparison,
  handleMethodChangeComparison,
  currentMethods,
  renderOptionsDynamic,
  currentSampleNameComparison,
  handleSampleNameChangeComparison,
  currentSampleNames,
  currentParameterComparison,
  handleParameterChangeComparison,
  currentParametersDynamic,
  currentInstrumentNames,
  currentInstrumentDots,
  currentInstrumentComparisonData,
  currentInstrument,
  currentLocation,
  summaries,
  hideRegionArea,
  tz,
  dateTimeFormat
}) => {
  const [keyChart, setKeyChart] = useState(
    `${currentLocation}-${currentMethodComparison}-${currentInstrument}-${currentSampleNameComparison}-${currentParameterComparison}`
  );

  useEffect(() => {
    setKeyChart(
      `${currentLocation}-${currentMethodComparison}-${currentInstrument}-${currentSampleNameComparison}-${currentParameterComparison}`
    );
  }, [currentInstrumentComparisonData]);

  // Calculate maxUCL and minLCL from instrument comparison data
  const { maxUCL, minLCL, yMax, yMin } = useMemo(() => {
    if (!currentInstrumentComparisonData || currentInstrumentComparisonData.length === 0) {
      return { maxUCL: undefined, minLCL: undefined, yMax: 0, yMin: 0 };
    }

    // Calculate control limits from summaries
    let maxUCL = -Infinity;
    let minLCL = Infinity;

    if (summaries?.length) {
      const relevantSummaries = summaries.filter(
        (summary) =>
          summary.location === currentLocation &&
          summary.method === currentMethodComparison &&
          summary.sampleName === currentSampleNameComparison &&
          summary.parameter === currentParameterComparison
      );

      relevantSummaries.forEach((summary) => {
        if (summary.ucl_sd !== undefined && summary.ucl_sd !== null) {
          const ucl = Number(summary.ucl_sd);
          if (Number.isFinite(ucl) && ucl > maxUCL) maxUCL = ucl;
        }
        if (summary.lcl_sd !== undefined && summary.lcl_sd !== null) {
          const lcl = Number(summary.lcl_sd);
          if (Number.isFinite(lcl) && lcl < minLCL) minLCL = lcl;
        }
      });
    }

    // Calculate y-axis range from data
    const currentInstrumentComparisonResults = currentInstrumentComparisonData
      .map((d) => Number(d.result))
      .filter((result) => Number.isFinite(result));

    const maxResult =
      currentInstrumentComparisonResults?.length > 0
        ? Math.max(...currentInstrumentComparisonResults)
        : -Infinity;
    const minResult =
      currentInstrumentComparisonResults?.length > 0
        ? Math.min(...currentInstrumentComparisonResults)
        : Infinity;

    const yMin = Number.isFinite(minResult)
      ? Math.min(minResult, Number.isFinite(minLCL) ? minLCL : minResult)
      : Number.isFinite(minLCL)
        ? minLCL
        : 0;
    const yMax = Number.isFinite(maxResult)
      ? Math.max(maxResult, Number.isFinite(maxUCL) ? maxUCL : maxResult)
      : Number.isFinite(maxUCL)
        ? maxUCL
        : 0;

    const topYInterval = yMax - yMin;
    const padding = topYInterval > 0 ? 0.05 * topYInterval : Math.abs(yMax) * 0.1 || 1;

    return {
      maxUCL: Number.isFinite(maxUCL) ? maxUCL : undefined,
      minLCL: Number.isFinite(minLCL) ? minLCL : undefined,
      yMax: yMax + padding,
      yMin: yMin - padding
    };
  }, [
    currentInstrumentComparisonData,
    summaries,
    currentLocation,
    currentMethodComparison,
    currentSampleNameComparison,
    currentParameterComparison
  ]);

  if (localSearchFinished) {
    return (
      <div>
        <Row className="instruments-chart-container">
          <Col lg={4}>
            <h3>Analyst Comparison</h3>
          </Col>
        </Row>
        <SQCAnalystChart
          xAxis="updatedIndex"
          analysts={currentPersonnelNames}
          analystsDots={currentPersonnelDots}
          hideRegionArea={!!hideRegionArea}
          syncId="chained"
          key={`analyst-comparison-${keyChart}`}
          yMin={chartScalars?.cYMin}
          yMax={chartScalars?.cYMax}
          data={chartData}
          showDateTimeInTooltip
          tz={tz}
          dateTimeFormat={dateTimeFormat}
          lines={[
            {
              yAxis: 'resultOmits',
              name: 'Data',
              color: '#32CD32'
            },
            {
              yAxis: 'mean',
              name: 'Average',
              reference: chartData?.length > 20,
              label: ``,
              color: '#ffb300'
            },
            {
              yAxis: 'sd3n',
              name: 'LCL',
              label: `LCL: ${renderChartLabelValue(chartData, 'sd3n')}`,
              overrideValue: Number(renderChartLabelValue(chartData, 'sd3n')),
              color: '#f56f42',
              reference: true
            },
            {
              yAxis: 'sd3p',
              name: 'UCL',
              label: `UCL: ${renderChartLabelValue(chartData, 'sd3p')}`,
              color: '#f56f42',
              reference: true,
              overrideValue: Number(renderChartLabelValue(chartData, 'sd3p'))
            }
          ]}
        />
        <Row className="instruments-chart-container">
          <Col lg={4}>
            <h3>Instrument Comparison</h3>
          </Col>
        </Row>
        <Row className="instrument-dropdowns" style={{ display: 'none' }}>
          <Form.Group as={Col} controlId="method">
            <Form.Label>Method</Form.Label>
            <Form.Select value={currentMethodComparison} onChange={handleMethodChangeComparison}>
              <option value="">Select method</option>
              {renderOptionsDynamic(currentMethods)}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} controlId="sampleName">
            <Form.Label>Sample Name</Form.Label>
            <Form.Select
              value={currentSampleNameComparison}
              onChange={handleSampleNameChangeComparison}
            >
              <option value="">Select sample name</option>
              {renderOptionsDynamic(currentSampleNames)}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} controlId="parameter">
            <Form.Label>Parameter</Form.Label>
            <Form.Select
              value={currentParameterComparison}
              onChange={handleParameterChangeComparison}
            >
              <option value="">Select parameter</option>
              {renderOptionsDynamic(currentParametersDynamic)}
            </Form.Select>
          </Form.Group>
        </Row>
        <div>
          <SQCInstrumentsChart
            xAxis="dataId"
            instrumentsArray={currentInstrumentNames}
            instrumentsDots={currentInstrumentDots}
            hideRegionArea={!!hideRegionArea}
            syncId="chained"
            lineType="linear"
            key={`instrument-comparison-${keyChart}`}
            yMin={yMin}
            yMax={yMax}
            data={currentInstrumentComparisonData}
            showDateTimeInTooltip
            tz={tz}
            dateTimeFormat={dateTimeFormat}
            maxUCL={maxUCL}
            minLCL={minLCL}
            lines={[
              {
                yAxis: 'resultOmits',
                name: 'Data',
                color: '#32CD32'
              }
            ]}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="please-select-container">
      Please select a location, method, sample name, instrument, and parameter
    </div>
  );
};

export default ComparisonSection;
